import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, type ReactNode } from "react";
import { setMiddlewareReadOnly } from "@/api/middleware";
import { qk } from "@/api/queryKeys";
import type { SupportSession } from "@/api/support";
import { getTenantPublic } from "@/api/tenants";
import { SessionOverride, useSession } from "@/app/session";
import { BrandOverride, brandFromPublic } from "@/app/tenant-provider";
import { ErrorState } from "@/components/data/error-state";
import { FullScreenLoadingState } from "@/components/data/loading-state";
import { SupportBanner } from "@/features/support/components/support-banner";

/**
 * Shows an organization's own app to a platform admin: its branding, as its admin, read-only, with the
 * banner on top. The database decides what the admin may read (their live session) and refuses every write.
 */
export function SupportMode({ session, children }: { session: SupportSession; children: ReactNode }) {
  const real = useSession();
  const email = real.status === "super_admin" ? real.email : "";

  const tenant = useQuery({
    queryKey: qk.tenant.public(session.tenantSlug),
    queryFn: () => getTenantPublic(session.tenantSlug),
    staleTime: 0,
  });

  useEffect(() => {
    setMiddlewareReadOnly(true);
    return () => setMiddlewareReadOnly(false);
  }, []);

  const view = useMemo(() => ({ status: "admin" as const, email, readOnly: true as const }), [email]);

  if (tenant.isPending) {
    return <FullScreenLoadingState />;
  }
  if (tenant.isError || !tenant.data) {
    return <ErrorState className="min-h-dvh" message="Couldn't load that organization." onRetry={() => void tenant.refetch()} />;
  }

  return (
    <SessionOverride value={view}>
      <BrandOverride brand={brandFromPublic(session.tenantSlug, tenant.data)}>
        {children}
        <SupportBanner session={session} />
      </BrandOverride>
    </SessionOverride>
  );
}
